import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { getStudyContent, markStudyComplete, submitGateAnswers, getFeatureFlags } from '../../services/userApi';
import PageMeta from '../../components/common/PageMeta';

const NAVY = '#1B3A5C';
const RED  = '#C1272D';

// ── Icons ─────────────────────────────────────────────────────────────────────
const Ico = {
  back:    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  check:   (c='#16A34A',s=16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  clock:   <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  chevron: (open) => <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open?'rotate(180deg)':'none', transition:'transform 0.2s'}}><polyline points="6 9 12 15 18 9"/></svg>,
  book:    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
};

const STYLE_MAP = {
  green: { bg:'#D1FAE5', text:'#065F46', border:'#6EE7B7' },
  red:   { bg:'#FEE2E2', text:'#991B1B', border:'#FCA5A5' },
  blue:  { bg:'#DBEAFE', text:'#1E40AF', border:'#93C5FD' },
};

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ icon, title, accent = NAVY, children }) {
  return (
    <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden', marginBottom:18 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'16px 22px', borderBottom:'1px solid #F3F2EE', background:'#FAFAF9' }}>
        <span style={{ width:32, height:32, borderRadius:8, background: accent+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{icon}</span>
        <h2 style={{ margin:0, fontSize:15, fontWeight:800, color:'#12110F', letterSpacing:'-0.01em' }}>{title}</h2>
      </div>
      <div style={{ padding:'18px 22px' }}>{children}</div>
    </div>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function Collapsible({ icon, title, accent = NAVY, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden', marginBottom:18 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'16px 22px', background:'#FAFAF9', border:'none', cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
        <span style={{ width:32, height:32, borderRadius:8, background: accent+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{icon}</span>
        <h2 style={{ margin:0, fontSize:15, fontWeight:800, color:'#12110F', flex:1, letterSpacing:'-0.01em' }}>{title}</h2>
        {Ico.chevron(open)}
      </button>
      {open && <div style={{ padding:'18px 22px', borderTop:'1px solid #F3F2EE' }}>{children}</div>}
    </div>
  );
}

// ── Gate quiz ─────────────────────────────────────────────────────────────────
function GateQuiz({ questions, subChapterId, onPassed, onSkip }) {
  const [answers, setAnswers] = useState({});
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const allAnswered = questions.every((_, i) => answers[i] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) return toast.error('Answer all 3 questions first');
    setLoading(true);
    try {
      const { data } = await submitGateAnswers(subChapterId, questions.map((_, i) => answers[i]));
      setResult(data);
      if (data.passed) { toast.success('All correct — unlocking quiz…'); setTimeout(onPassed, 800); }
    } catch { toast.error('Submission failed — try again'); }
    finally { setLoading(false); }
  };

  if (result && !result.passed) {
    return (
      <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:12, padding:24, textAlign:'center' }}>
        <div style={{ fontSize:40, marginBottom:8 }}>📖</div>
        <p style={{ margin:'0 0 4px', fontWeight:800, fontSize:16, color:'#92400E' }}>{result.correct}/{result.total} correct</p>
        <p style={{ margin:'0 0 20px', fontSize:13, color:'#B45309' }}>Review the worked example and case study, then try again.</p>
        <button onClick={() => { setResult(null); setAnswers({}); }}
          style={{ padding:'10px 28px', background:NAVY, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <p style={{ margin:'0 0 20px', fontSize:14, color:'#44403C', lineHeight:1.65 }}>
        Get all 3 right to unlock the quiz — or click <strong>"I've read it"</strong> to go straight through.
      </p>
      {questions.map((q, i) => (
        <div key={i} style={{ marginBottom:22 }}>
          <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:14, color:'#12110F', lineHeight:1.5 }}>
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', background:NAVY, color:'#fff', fontSize:11, fontWeight:800, marginRight:8, flexShrink:0 }}>{i+1}</span>
            {q.question}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:8, paddingLeft:30 }}>
            {q.options.map((opt, j) => (
              <label key={j} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, cursor:'pointer', border:`1.5px solid ${answers[i]===j ? NAVY : '#E5E2D9'}`, background: answers[i]===j ? '#EEF4FF' : '#fff', fontSize:13, color:'#12110F', fontWeight: answers[i]===j ? 600 : 400, transition:'all 0.12s' }}>
                <input type="radio" name={`gq-${i}`} value={j} checked={answers[i]===j} onChange={() => setAnswers(p => ({...p,[i]:j}))} style={{ accentColor:NAVY }} />
                {opt}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display:'flex', gap:10, marginTop:4, flexWrap:'wrap' }}>
        <button onClick={handleSubmit} disabled={!allAnswered || loading}
          style={{ padding:'11px 28px', background: allAnswered ? NAVY : '#CBD5E1', color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor: allAnswered ? 'pointer' : 'not-allowed', transition:'background 0.15s' }}>
          {loading ? 'Checking…' : 'Submit answers'}
        </button>
        <button onClick={onSkip}
          style={{ padding:'11px 20px', background:'transparent', color:'#78756E', border:'1px solid #E5E2D9', borderRadius:10, fontWeight:500, fontSize:13, cursor:'pointer' }}>
          I've read it — skip to quiz
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StudyChapterPage() {
  const { subChapterId } = useParams();
  const navigate = useNavigate();
  const [features, setFeatures] = useState({ flashcards: true });
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  useEffect(() => { getFeatureFlags().then(r => setFeatures(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await getStudyContent(subChapterId);
        setData(res);
      } catch (err) {
        const code = err.response?.data?.code;
        if (code === 'SUBSCRIPTION_REQUIRED') { toast.error('Subscription required'); navigate(`/subscribe/${err.response.data.role_slug}`); }
        else if (code === 'AUTH_REQUIRED') { toast.error('Please log in first'); navigate('/login'); }
        else { toast.error('Failed to load study material'); navigate(-1); }
      } finally { setLoading(false); }
    })();
  }, [subChapterId, navigate]);

  const handleMarkAndContinue = async () => {
    setMarking(true);
    try { await markStudyComplete(subChapterId); navigate(`/quiz/${subChapterId}`); }
    catch { toast.error('Failed to record completion'); setMarking(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F5F4F0' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:32, height:32, border:`3px solid ${NAVY}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!data) return null;
  const { sub_chapter: sc, content, completion } = data;
  const alreadyDone = completion?.gate_passed || false;

  // Normalise schema — handle both old (worked_example/case_study) and new (example) formats
  const concepts  = content.key_concepts || content.key_types || [];
  const formulas  = content.formulas || [];
  const example   = content.example || null;
  const oldExample = content.worked_example || null;
  const caseStudy  = content.case_study || null;

  return (
    <div style={{ background:'#F5F4F0', minHeight:'100vh' }}>
      <PageMeta
        title={`${sc.title} — ${sc.role_title} Study Guide`}
        description={`Study guide for ${sc.title} (${sc.role_title}). Chapter ${sc.chapter_number}.${sc.sub_chapter_number} with key concepts, examples, and practice quiz.`}
        noIndex={true}
      />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} * { box-sizing:border-box; }`}</style>

      {/* ── Hero header ── */}
      <div style={{ background:`linear-gradient(135deg, ${NAVY} 0%, #0F2340 100%)`, color:'#fff', padding:'24px 0 28px' }}>
        <div style={{ maxWidth:800, margin:'0 auto', padding:'0 24px' }}>
          <button onClick={() => navigate(`/roles/${sc.role_slug}`)} style={{ background:'none', border:'none', color:'#94A3B8', cursor:'pointer', padding:0, fontSize:13, display:'flex', alignItems:'center', gap:4, marginBottom:16 }}>
            {Ico.back} Back to role
          </button>
          {/* Breadcrumb */}
          <p style={{ margin:'0 0 10px', fontSize:12, color:'#64748B', fontWeight:500, letterSpacing:'0.04em', textTransform:'uppercase' }}>
            {sc.role_title} &nbsp;›&nbsp; Chapter {sc.chapter_number}: {sc.chapter_title}
          </p>
          {/* Title */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
              {Ico.book}
            </div>
            <div>
              <h1 style={{ margin:'0 0 8px', fontSize:22, fontWeight:900, lineHeight:1.3, letterSpacing:'-0.02em' }}>
                {sc.title}
              </h1>
              <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#94A3B8' }}>
                  {Ico.clock} ~{content.reading_time_mins} min read
                </span>
                <span style={{ width:4, height:4, borderRadius:'50%', background:'#475569' }} />
                <span style={{ fontSize:12, color:'#94A3B8' }}>
                  Sub-chapter {sc.chapter_number}.{sc.sub_chapter_number}
                </span>
                {alreadyDone && (
                  <>
                    <span style={{ width:4, height:4, borderRadius:'50%', background:'#475569' }} />
                    <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#4ADE80', fontWeight:600 }}>
                      {Ico.check('#4ADE80', 13)} Completed
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick actions bar ── */}
      {features.flashcards && concepts.length > 0 && (
        <div style={{ background:'#0F2340', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ maxWidth:800, margin:'0 auto', padding:'10px 24px', display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'rgba(255,255,255,0.4)', fontWeight:500, marginRight:4 }}>Quick study:</span>
            <Link to={`/flashcards/${subChapterId}`}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.8)', fontSize:12, fontWeight:600, textDecoration:'none', transition:'all 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; e.currentTarget.style.color='#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.color='rgba(255,255,255,0.8)'; }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              Flash cards ({concepts.length})
            </Link>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ maxWidth:800, margin:'0 auto', padding:'28px 24px 64px' }}>

        {/* Overview */}
        <Section icon="📘" title="Overview" accent="#1E40AF">
          {content.overview.split('\n\n').map((p, i) => (
            <p key={i} style={{ margin: i === 0 ? '0 0 14px' : '0 0 14px', fontSize:14, color:'#374151', lineHeight:1.8 }}>
              {i === 0
                ? <><span style={{ fontSize:15, fontWeight:700, color:'#12110F' }}>{p.slice(0, p.indexOf(' ', 80))}</span>{p.slice(p.indexOf(' ', 80))}</>
                : p}
            </p>
          ))}
        </Section>

        {/* Key Concepts */}
        <Section icon="🔑" title="Key Concepts" accent="#7C3AED">
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {concepts.map((kt, i) => {
              const c = STYLE_MAP[kt.style] || STYLE_MAP.blue;
              return (
                <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'12px 14px', borderRadius:10, border:`1px solid ${c.border}`, background: c.bg }}>
                  <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:800, background:'#fff', color: c.text, border:`1px solid ${c.border}`, whiteSpace:'nowrap', flexShrink:0, marginTop:1, letterSpacing:'0.02em' }}>
                    {kt.name}
                  </span>
                  <p style={{ margin:0, fontSize:13, color:'#374151', lineHeight:1.65 }}>{kt.description}</p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Formulas */}
        {formulas.length > 0 && (
          <Section icon="📐" title="Formulas & Frameworks" accent="#0F766E">
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {formulas.map((f, i) => (
                <div key={i} style={{ borderLeft:`4px solid ${NAVY}`, paddingLeft:16, paddingTop:4, paddingBottom:4 }}>
                  <p style={{ margin:'0 0 6px', fontWeight:800, fontSize:14, color:'#12110F' }}>{f.name}</p>
                  <div style={{ fontFamily:'monospace', fontSize:13, background:'#F0F4FF', padding:'8px 12px', borderRadius:8, color:NAVY, fontWeight:700, marginBottom:10, border:'1px solid #C7D2FE' }}>
                    {f.formula}
                  </div>
                  {/* New schema: when_to_use. Old schema: favourable/adverse */}
                  {f.when_to_use && (
                    <span style={{ fontSize:12, padding:'4px 10px', borderRadius:8, background:'#F0FDF4', color:'#166534', fontWeight:600, border:'1px solid #86EFAC' }}>
                      When to use: {f.when_to_use}
                    </span>
                  )}
                  {f.favourable && (
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, padding:'4px 10px', borderRadius:8, background:'#D1FAE5', color:'#065F46', fontWeight:600, border:'1px solid #6EE7B7' }}>▲ Favourable: {f.favourable}</span>
                      <span style={{ fontSize:12, padding:'4px 10px', borderRadius:8, background:'#FEE2E2', color:'#991B1B', fontWeight:600, border:'1px solid #FCA5A5' }}>▼ Adverse: {f.adverse}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Advanced note (old schema only) + interview tip */}
        <div style={{ display:'grid', gridTemplateColumns: content.advanced_note ? '1fr 1fr' : '1fr', gap:14, marginBottom:18 }}>
          {content.advanced_note && (
            <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:14, padding:'18px 18px' }}>
              <p style={{ margin:'0 0 6px', fontWeight:800, fontSize:13, color:'#3730A3', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:16 }}>💡</span> Advanced Note
              </p>
              <p style={{ margin:0, fontSize:13, color:'#3730A3', lineHeight:1.7 }}>{content.advanced_note}</p>
            </div>
          )}
          {content.interview_tip && (
            <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:14, padding:'18px 18px' }}>
              <p style={{ margin:'0 0 6px', fontWeight:800, fontSize:13, color:'#92400E', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:16 }}>🎯</span> Interview Tip
              </p>
              <p style={{ margin:0, fontSize:13, color:'#92400E', lineHeight:1.7 }}>{content.interview_tip}</p>
            </div>
          )}
        </div>

        {/* New schema: slim example */}
        {example && (
          <Section icon="🏢" title={`Real-World Example — ${example.company}`} accent="#0F766E">
            <p style={{ margin:'0 0 14px', fontSize:13, color:'#374151', lineHeight:1.7, padding:'10px 14px', background:'#F0FDF9', borderRadius:8, border:'1px solid #99F6E4' }}>
              {example.scenario}
            </p>
            <p style={{ margin:'0 0 8px', fontWeight:800, fontSize:13, color:'#12110F' }}>Key takeaways</p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
              {example.takeaways.map((t, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                  <span style={{ width:22, height:22, borderRadius:'50%', background:NAVY, color:'#fff', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>{i+1}</span>
                  <p style={{ margin:0, fontSize:13, color:'#374151', lineHeight:1.65 }}>{t}</p>
                </div>
              ))}
            </div>
            <div style={{ background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:10, padding:'12px 16px', display:'flex', gap:10 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>✅</span>
              <p style={{ margin:0, fontSize:13, color:'#166534', lineHeight:1.7 }}>{example.lesson}</p>
            </div>
          </Section>
        )}

        {/* Old schema: full worked example with table */}
        {!example && oldExample && (
          <Collapsible icon="🔢" title={`Worked Example — ${oldExample.title}`} accent="#0F766E" defaultOpen>
            <p style={{ margin:'0 0 14px', fontSize:13, color:'#374151', lineHeight:1.7, padding:'10px 14px', background:'#F0FDF9', borderRadius:8, border:'1px solid #99F6E4' }}>
              {oldExample.scenario}
            </p>

            {/* Table */}
            {oldExample.table?.length > 0 && (
              <div style={{ overflowX:'auto', marginBottom:16 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC' }}>
                      {['Item','Standard','Actual','Variance','Type'].map(h => (
                        <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontWeight:700, color:'#64748B', fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'2px solid #E2E8F0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {oldExample.table.map((row, i) => {
                      const tc = row.variance_type==='favourable' ? '#16A34A' : row.variance_type==='adverse' ? '#DC2626' : '#64748B';
                      const pill = row.variance_type==='favourable' ? '#DCFCE7' : row.variance_type==='adverse' ? '#FEE2E2' : '#F1F5F9';
                      return (
                        <tr key={i} style={{ background: i%2===0 ? '#fff' : '#FAFAFA', borderBottom:'1px solid #F1F5F9' }}>
                          <td style={{ padding:'9px 14px', fontWeight:700, color:'#12110F' }}>{row.item}</td>
                          <td style={{ padding:'9px 14px', color:'#374151' }}>{row.standard}</td>
                          <td style={{ padding:'9px 14px', color:'#374151' }}>{row.actual}</td>
                          <td style={{ padding:'9px 14px', color:tc, fontWeight:600 }}>{row.variance}</td>
                          <td style={{ padding:'9px 14px' }}>
                            <span style={{ padding:'3px 9px', borderRadius:99, fontSize:11, fontWeight:700, background:pill, color:tc }}>
                              {row.variance_type}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Steps */}
            {oldExample.solution_steps?.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <p style={{ margin:'0 0 10px', fontWeight:800, fontSize:13, color:'#12110F' }}>Step-by-step solution</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {oldExample.solution_steps.map((s, i) => (
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                      <span style={{ width:22, height:22, borderRadius:'50%', background:NAVY, color:'#fff', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>{i+1}</span>
                      <p style={{ margin:0, fontSize:13, color:'#374151', lineHeight:1.65 }}>{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conclusion */}
            {oldExample.conclusion && (
              <div style={{ background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:10, padding:'14px 16px', display:'flex', gap:10 }}>
                <span style={{ fontSize:18, flexShrink:0 }}>✅</span>
                <p style={{ margin:0, fontSize:13, color:'#166534', lineHeight:1.7 }}>{oldExample.conclusion}</p>
              </div>
            )}
          </Collapsible>
        )}

        {/* Old schema: case study */}
        {!example && caseStudy && (
          <Collapsible icon="🏢" title={`Case Study — ${caseStudy.company} (${caseStudy.industry})`} accent="#7C3AED">
            {/* Context banner */}
            <div style={{ background:'#F5F3FF', border:'1px solid #DDD6FE', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
              <p style={{ margin:'0 0 4px', fontWeight:700, fontSize:12, color:'#6D28D9', textTransform:'uppercase', letterSpacing:'0.05em' }}>Company Context</p>
              <p style={{ margin:0, fontSize:13, color:'#4C1D95', lineHeight:1.65 }}>{caseStudy.context}</p>
            </div>

            <p style={{ margin:'0 0 6px', fontWeight:700, fontSize:13, color:'#374151' }}>The Scenario</p>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#374151', lineHeight:1.7 }}>{caseStudy.scenario}</p>

            <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:13, color:'#374151' }}>Findings</p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
              {caseStudy.findings?.map((f, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'10px 12px', borderRadius:10, background: f.type==='issue' ? '#FFF7ED' : '#F0FDF4', border:`1px solid ${f.type==='issue' ? '#FED7AA' : '#86EFAC'}` }}>
                  <span style={{ fontSize:11, fontWeight:800, padding:'3px 8px', borderRadius:99, background: f.type==='issue' ? '#FDE68A' : '#BBF7D0', color: f.type==='issue' ? '#92400E' : '#166534', flexShrink:0, marginTop:1, textTransform:'uppercase', letterSpacing:'0.04em' }}>
                    {f.type}
                  </span>
                  <p style={{ margin:0, fontSize:13, color:'#374151', lineHeight:1.6 }}>{f.text}</p>
                </div>
              ))}
            </div>

            <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:10, padding:'14px 16px', display:'flex', gap:10 }}>
              <span style={{ fontSize:18, flexShrink:0 }}>🎤</span>
              <div>
                <p style={{ margin:'0 0 4px', fontWeight:700, fontSize:12, color:'#3730A3', textTransform:'uppercase', letterSpacing:'0.04em' }}>How to present in an interview</p>
                <p style={{ margin:0, fontSize:13, color:'#3730A3', lineHeight:1.7 }}>{caseStudy.lesson}</p>
              </div>
            </div>
          </Collapsible>
        )}

        {/* ── Gate quiz ── */}
        {!alreadyDone && content.gate_questions?.length > 0 && (
          <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden', marginBottom:18 }}>
            <div style={{ padding:'18px 22px', borderBottom:'1px solid #F3F2EE', background:'linear-gradient(135deg, #1B3A5C 0%, #0F2340 100%)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>🔓</span>
                <div>
                  <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#fff' }}>Gate Quiz</h2>
                  <p style={{ margin:0, fontSize:12, color:'#94A3B8' }}>Pass all 3 to unlock the interview quiz</p>
                </div>
              </div>
            </div>
            <div style={{ padding:'20px 22px' }}>
              <GateQuiz questions={content.gate_questions} subChapterId={subChapterId} onPassed={() => navigate(`/quiz/${subChapterId}`)} onSkip={handleMarkAndContinue} />
            </div>
          </div>
        )}

        {/* Already completed */}
        {alreadyDone && (
          <div style={{ background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:16, padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:28 }}>🎉</span>
              <div>
                <p style={{ margin:0, fontWeight:800, fontSize:15, color:'#166534' }}>Study material completed</p>
                <p style={{ margin:0, fontSize:13, color:'#166534', opacity:0.8 }}>You're ready for the interview quiz.</p>
              </div>
            </div>
            <button onClick={() => navigate(`/quiz/${subChapterId}`)}
              style={{ padding:'11px 28px', background:NAVY, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', whiteSpace:'nowrap' }}>
              Start quiz →
            </button>
          </div>
        )}

        {/* No gate questions fallback */}
        {!alreadyDone && (!content.gate_questions || content.gate_questions.length === 0) && (
          <button onClick={handleMarkAndContinue} disabled={marking}
            style={{ width:'100%', padding:16, background:NAVY, color:'#fff', border:'none', borderRadius:14, fontWeight:700, fontSize:15, cursor:'pointer' }}>
            {marking ? 'Loading…' : "I've read this — start quiz →"}
          </button>
        )}
      </div>
    </div>
  );
}
